import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  LogIn,
  Mail,
  CheckCircle2,
  PlayCircle,
  Loader2,
} from "lucide-react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

// ---------- helpers ----------

function youtubeEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    let id = "";
    if (u.hostname.includes("youtu.be")) {
      id = u.pathname.replace("/", "");
    } else if (u.searchParams.get("v")) {
      id = u.searchParams.get("v");
    } else if (u.pathname.startsWith("/embed/")) {
      id = u.pathname.split("/")[2];
    } else if (u.pathname.startsWith("/shorts/")) {
      id = u.pathname.split("/")[2];
    }
    return id ? `https://www.youtube.com/embed/${id}` : null;
  } catch {
    return null;
  }
}

// Section wrapper: slides up + fades in once it enters the viewport.
function RevealSection({ children, index = 0, id }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            io.disconnect();
          }
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <section
      id={id}
      ref={ref}
      data-testid={`landing-section-${id}`}
      style={{ transitionDelay: `${Math.min(index, 4) * 60}ms` }}
      className={`transition-all duration-[700ms] ease-out ${
        shown ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
      }`}
    >
      {children}
    </section>
  );
}

// ---------- section renderers ----------

function HeroSection({ data, brand }) {
  const d = data || {};
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-white">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-blue-100/60 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full bg-blue-200/40 blur-3xl"
      />
      <div className="relative max-w-6xl mx-auto px-6 py-24 lg:py-32 text-center">
        {d.eyebrow && (
          <div className="inline-block mb-5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium tracking-wide border border-blue-100">
            {d.eyebrow}
          </div>
        )}
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-900 leading-[1.08] max-w-4xl mx-auto"
          data-testid="landing-headline"
        >
          {d.headline || "Tired of making invoices one by one? BulkInvoice does it in seconds."}
        </h1>
        {d.subheadline && (
          <p className="mt-6 text-base sm:text-lg text-zinc-600 max-w-2xl mx-auto leading-relaxed">
            {d.subheadline}
          </p>
        )}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Button
            asChild
            size="lg"
            className="h-12 px-6 bg-blue-600 hover:bg-blue-700 text-base shadow-lg shadow-blue-600/20"
            data-testid="landing-cta-primary"
          >
            <Link to={d.primary_cta_href || "/signup"}>
              {d.primary_cta_text || "Start Free Trial"}
              <ArrowRight size={16} className="ml-2" />
            </Link>
          </Button>
          {d.secondary_cta_text && (
            <Button
              asChild
              variant="outline"
              size="lg"
              className="h-12 px-6 text-base border-zinc-300"
              data-testid="landing-cta-secondary"
            >
              <Link to={d.secondary_cta_href || "/login"}>
                <LogIn size={16} className="mr-2" />
                {d.secondary_cta_text}
              </Link>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function VideoSection({ data }) {
  const d = data || {};
  const embed = youtubeEmbed(d.youtube_url);
  return (
    <div className="max-w-6xl mx-auto px-6 py-20">
      <div className="text-center mb-10">
        {d.eyebrow && (
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
            {d.eyebrow}
          </div>
        )}
        {d.title && (
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
            {d.title}
          </h2>
        )}
        {d.subtitle && (
          <p className="mt-3 text-zinc-500 text-base">{d.subtitle}</p>
        )}
      </div>
      <div className="relative mx-auto max-w-4xl aspect-video rounded-2xl overflow-hidden border border-zinc-200 bg-zinc-100 shadow-xl shadow-blue-900/5">
        {embed ? (
          <iframe
            src={embed}
            title={d.title || "Video"}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            data-testid="landing-video-frame"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-400 gap-2 text-sm">
            <PlayCircle size={46} />
            Add a YouTube URL from Admin to embed a video here
          </div>
        )}
      </div>
    </div>
  );
}

function FeaturesSection({ data }) {
  const d = data || {};
  const feats = Array.isArray(d.features) ? d.features : [];
  return (
    <div className="bg-zinc-50/60 border-y border-zinc-200/60">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          {d.eyebrow && (
            <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
              {d.eyebrow}
            </div>
          )}
          {d.title && (
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
              {d.title}
            </h2>
          )}
          {d.subtitle && (
            <p className="mt-3 text-zinc-500 max-w-xl mx-auto">{d.subtitle}</p>
          )}
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {feats.map((f, i) => (
            <div
              key={i}
              className="rounded-xl bg-white border border-zinc-200 p-7 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-600/5 transition-all"
              data-testid={`landing-feature-${i}`}
            >
              <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center font-bold mono">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="mt-4 font-semibold text-zinc-900 text-lg">{f.title}</h3>
              <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PricingSection({ data }) {
  const d = data || {};
  return (
    <div className="max-w-6xl mx-auto px-6 py-20">
      <div className="text-center mb-10">
        {d.eyebrow && (
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
            {d.eyebrow}
          </div>
        )}
        {d.title && (
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
            {d.title}
          </h2>
        )}
        {d.subtitle && (
          <p className="mt-3 text-zinc-500 max-w-xl mx-auto">{d.subtitle}</p>
        )}
      </div>
      <div className="mx-auto max-w-md">
        <div className="rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 text-white p-8 shadow-2xl shadow-blue-600/20">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-100">
            {d.plan_name || "Monthly"}
          </div>
          <div className="mt-4 flex items-baseline gap-1.5">
            <div className="text-5xl font-bold tracking-tight">{d.price_text || "₹999"}</div>
            <div className="text-blue-100">{d.price_period || "/ month"}</div>
          </div>
          <ul className="mt-7 space-y-3 text-sm">
            {d.included && (
              <li className="flex items-start gap-2.5">
                <CheckCircle2 size={18} className="shrink-0 text-blue-200 mt-0.5" />
                <span>{d.included}</span>
              </li>
            )}
            {d.overage && (
              <li className="flex items-start gap-2.5">
                <CheckCircle2 size={18} className="shrink-0 text-blue-200 mt-0.5" />
                <span>{d.overage}</span>
              </li>
            )}
          </ul>
          <Button
            asChild
            size="lg"
            className="w-full mt-8 h-12 bg-white text-blue-700 hover:bg-blue-50 font-semibold"
            data-testid="landing-pricing-cta"
          >
            <Link to={d.cta_href || "/signup"}>
              {d.cta_text || "Start Free Trial"}
              <ArrowRight size={16} className="ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function FooterSection({ data, brand }) {
  const d = data || {};
  return (
    <footer className="bg-zinc-900 text-zinc-300" data-testid="landing-footer">
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex flex-col md:flex-row justify-between gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center text-white font-bold">B</div>
              <div className="font-bold text-white">{brand?.name || "BulkInvoice"}</div>
            </div>
            {d.tagline && <p className="text-sm text-zinc-400 max-w-sm">{d.tagline}</p>}
          </div>
          <div className="flex flex-col gap-2 text-sm">
            {d.support_email && (
              <a
                href={`mailto:${d.support_email}`}
                className="flex items-center gap-2 text-zinc-300 hover:text-white"
                data-testid="landing-footer-email"
              >
                <Mail size={14} />
                {d.support_email}
              </a>
            )}
            <Link to="/login" className="text-zinc-400 hover:text-white">
              Sign in
            </Link>
            <Link to="/signup" className="text-zinc-400 hover:text-white">
              Create an account
            </Link>
          </div>
        </div>
        {d.copyright && (
          <div className="border-t border-zinc-800 mt-10 pt-6 text-xs text-zinc-500">
            {d.copyright}
          </div>
        )}
      </div>
    </footer>
  );
}

function TextSection({ data }) {
  const d = data || {};
  return (
    <div className="max-w-3xl mx-auto px-6 py-16 text-center">
      {d.eyebrow && (
        <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-2">
          {d.eyebrow}
        </div>
      )}
      {d.title && (
        <h2 className="text-3xl font-bold tracking-tight text-zinc-900">{d.title}</h2>
      )}
      {d.body && <p className="mt-4 text-zinc-500 leading-relaxed">{d.body}</p>}
    </div>
  );
}

const SECTION_RENDERERS = {
  hero: HeroSection,
  video: VideoSection,
  features: FeaturesSection,
  pricing: PricingSection,
  footer: FooterSection,
  text: TextSection,
};

// ---------- page ----------

export default function Landing() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/landing");
        if (active) setConfig(data);
      } catch {
        // Fallback to empty — page will still render brand/nav
        if (active) setConfig({ brand: { name: "BulkInvoice" }, sections: [] });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-400 text-sm gap-2">
        <Loader2 size={16} className="animate-spin" /> Loading…
      </div>
    );
  }

  const brand = config?.brand || {};
  const sections = Array.isArray(config?.sections) ? config.sections : [];

  return (
    <div className="min-h-screen bg-white" data-testid="landing-root">
      {/* Top nav */}
      <nav className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-zinc-200/70">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
            <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center text-white font-bold">
              B
            </div>
            <div className="font-bold text-zinc-900 tracking-tight">
              {brand.name || "BulkInvoice"}
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="ghost"
              size="sm"
              className="h-9"
              data-testid="landing-nav-signin"
            >
              <Link to={brand.nav_cta_href || "/login"}>
                {brand.nav_cta_text || "Sign In"}
              </Link>
            </Button>
            <Button
              asChild
              size="sm"
              className="h-9 bg-blue-600 hover:bg-blue-700"
              data-testid="landing-nav-trial"
            >
              <Link to="/signup">
                Start Free Trial
                <ArrowRight size={14} className="ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </nav>

      {sections
        .filter((s) => s.visible !== false && SECTION_RENDERERS[s.type])
        .map((s, i) => {
          const Comp = SECTION_RENDERERS[s.type];
          return (
            <RevealSection key={s.id || i} id={s.id || `s-${i}`} index={i}>
              <Comp data={s.data} brand={brand} />
            </RevealSection>
          );
        })}
    </div>
  );
}
